import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { BlankCell } from '../../components/Cells/BlankCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { getFileTypeLabel } from '../../enums/FileType';
import { getImageTypeLabel } from '../../enums/ImageType';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { Location } from '../models/Location';
import { LocationVariant, LocationsService } from '../services/LocationsService';

type State = PaginatedTableState & {};

type Props = {
  variantId: number;
  variant: LocationVariant;
  initialData?: PaginationResult<Location> | null;
  parentLoading?: boolean;
  withPrefill?: boolean;
  defaultPageSize?: number;
  className?: string;
};

export const DEFAULT_LOCATIONS_PAGE_SIZE = 10;

export class LocationsTable extends PaginatedTableController<Location, Props, State> {
  private readonly locationService: LocationsService;

  static defaultProps = {
    initialData: null,
    parentLoading: false,
    withPrefill: false,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.locationService = new LocationsService();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.locationService.cancelAllRequests();
  }

  protected isLoading(): boolean {
    return super.isLoading() || Boolean(this.props.parentLoading);
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Location>> {
    let dataGridProps = super.getAdditionalDataGridProps();
    if (this.props.className) {
      dataGridProps = { ...dataGridProps, className: this.props.className };
    }
    return dataGridProps;
  }

  protected getPrefilledConfig() {
    if (!this.props.withPrefill) {
      return super.getPrefilledConfig();
    }

    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<Location>;
  }

  protected getDefaultPageSize(): number {
    return this.props.defaultPageSize ?? DEFAULT_LOCATIONS_PAGE_SIZE;
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('locations') };
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getFieldConfigurations(): FieldConfiguration<Location>[] {
    const columns: FieldConfiguration<Location>[] = [
      {
        fieldKey: 'path',
        label: t('path'),
        description: t('path'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getValue().getPath()}</TypographyOverflow>,
      },
      {
        fieldKey: 'hostName',
        label: t('hostName'),
        description: t('hostName'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getValue().getHostname()}</TypographyOverflow>,
      },
      {
        fieldKey: 'fileType',
        label: t('fileType'),
        description: t('fileType'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getFileTypeLabel(model.getValue().getFileType())}</TypographyOverflow>,
      },
    ];
    const dataHasImage = this.data.length && this.data[0].getValue().getImageInfo() && this.data[0].getValue().getImageType();
    if (dataHasImage) {
      columns.push({
        fieldKey: 'imageInfo',
        label: t('imageInfo'),
        description: t('imageInfo'),
        render: (model, { hasFocus }) => {
          const imageInfo = model.getValue().getImageInfo();
          const imageType = model.getValue().getImageType();
          if (!imageInfo || !imageType) {
            return <BlankCell blankText={t('none')} />;
          }
          const imageTypeLabel = getImageTypeLabel(imageType);

          return <TypographyOverflow hasFocus={hasFocus}>{t('imageInfoDetail', { imageInfo, imageType: imageTypeLabel })}</TypographyOverflow>;
        },
      });
    }

    return columns;
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Location>> {
    try {
      return await this.locationService.getLocations(this.props.variantId, this.props.variant, cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
